import { useEffect, useState, useRef, useMemo } from "react";
import { api } from "../../lib/api";
import { MessageSquare, Send, ArrowLeft, FileText, ExternalLink, Search } from "lucide-react";

export function MessagesPanel({ onOpenBooking }) {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [reply, setReply] = useState("");
  const [q, setQ] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const loadThreads = () => api.get("/admin/message-threads").then(r => setThreads(r.data.items || [])).catch(() => {});
  useEffect(() => { loadThreads(); }, []);

  const scrollToBottom = () => requestAnimationFrame(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  });

  const open = async (ref) => {
    setActive(ref);
    const r = await api.get(`/admin/messages/${ref}`);
    setMsgs(r.data.items || []);
    loadThreads();
    scrollToBottom();
  };
  const send = async () => {
    if (!reply.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/admin/message/${active}`, { body: reply });
      setReply("");
      const r = await api.get(`/admin/messages/${active}`);
      setMsgs(r.data.items || []);
      scrollToBottom();
    } finally { setSending(false); }
  };

  // Unread first, then most-recent activity.
  const sorted = useMemo(() => {
    const term = q.trim().toLowerCase();
    return [...threads]
      .filter(t => !term || [t.client_name, t.client_email, t.reference, t.last_body].some(v => (v || "").toLowerCase().includes(term)))
      .sort((a, b) => (b.unread > 0) - (a.unread > 0) || (b.last_at || "").localeCompare(a.last_at || ""));
  }, [threads, q]);

  if (active) {
    const t = threads.find(x => x.reference === active);
    return (
      <div data-testid="messages-thread">
        <div className="flex items-center justify-between mb-4">
          <button data-testid="messages-back" onClick={() => { setActive(null); loadThreads(); }} className="de-btn-secondary"><ArrowLeft size={16} /> All messages</button>
          {onOpenBooking && (
            <button data-testid="messages-open-jobfile" onClick={() => onOpenBooking(active)} className="text-xs font-bold uppercase tracking-widest text-[#C79A3B] inline-flex items-center gap-1.5 hover:text-[#A87E28]">
              <ExternalLink size={14} /> Open job file
            </button>
          )}
        </div>
        <div className="font-bold mb-1">{t?.client_name || active} <span className="font-mono text-[#C79A3B]">{active}</span>{t?.client_email && <span className="text-xs text-[#6B7280] font-normal ml-2">{t.client_email}</span>}</div>
        <div ref={scrollRef} className="border border-[#E5E7EB] bg-white p-4 max-h-[55vh] overflow-y-auto space-y-3 mb-4">
          {msgs.map(m => (
            <div key={m.id} className={`p-3 border max-w-[78%] ${m.from_ === "team" ? "bg-[#F4F4F4] border-[#E5E7EB] ml-auto" : "bg-white border-[#C79A3B] mr-auto"}`}>
              <div className="label-eyebrow mb-1">{m.from_ === "team" ? "The Extension Works" : "Client"} · {new Date(m.created_at).toLocaleString("en-GB")}</div>
              {m.body && <div className="text-sm whitespace-pre-wrap">{m.body}</div>}
              {m.attachments?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2" data-testid={`admin-msg-attachments-${m.id}`}>
                  {m.attachments.map(a => {
                    const url = `${api.defaults.baseURL}/admin/upload-file/${a.id}`;
                    const isImg = (a.content_type || "").startsWith("image/");
                    return isImg ? (
                      <a key={a.id} href={url} target="_blank" rel="noreferrer" data-testid={`admin-msg-att-${a.id}`}>
                        <img src={url} alt={a.filename || ""} className="w-20 h-20 object-cover border border-[#E5E7EB]" />
                      </a>
                    ) : (
                      <a key={a.id} href={url} target="_blank" rel="noreferrer" data-testid={`admin-msg-att-${a.id}`}
                         className="inline-flex items-center gap-2 border border-[#E5E7EB] px-2 py-1.5 text-xs bg-white hover:border-[#12263A]">
                        <FileText size={14} /> {a.filename || "file"}
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
          {msgs.length === 0 && <div className="text-sm text-[#6B7280]">No messages.</div>}
        </div>
        <div className="flex gap-2">
          <textarea data-testid="messages-reply" rows={2} value={reply} onChange={e => setReply(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Reply to the client…  (Enter to send, Shift+Enter for a new line)" className="flex-1 border border-[#E5E7EB] px-3 py-2 text-sm" />
          <button data-testid="messages-send" disabled={sending || !reply.trim()} onClick={send} className="de-btn-primary shrink-0 disabled:opacity-60"><Send size={16} /> {sending ? "Sending…" : "Send"}</button>
        </div>
      </div>
    );
  }

  const totalUnread = threads.reduce((n, t) => n + (t.unread || 0), 0);
  return (
    <div data-testid="messages-panel">
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <input data-testid="messages-search" value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search conversations by name, email or reference…"
            className="w-full border border-[#E5E7EB] bg-white pl-9 pr-3 py-2 text-sm" />
        </div>
        {totalUnread > 0 && <span className="text-xs font-bold text-[#C79A3B] whitespace-nowrap" data-testid="messages-total-unread">{totalUnread} unread</span>}
      </div>
      <div className="space-y-2">
        {sorted.map(t => (
          <button key={t.reference} data-testid={`message-thread-${t.reference}`} onClick={() => open(t.reference)}
            className={`w-full text-left border p-4 flex items-center gap-4 hover:bg-[#FBF3EC] ${t.unread > 0 ? "border-[#C79A3B] bg-[#FCF1EA]" : "border-[#E5E7EB] bg-white"}`}>
            <MessageSquare size={18} className={`shrink-0 ${t.unread > 0 ? "text-[#C79A3B]" : "text-[#6B7280]"}`} />
            <div className="flex-1 min-w-0">
              <div className="font-bold">{t.client_name || t.client_email || "—"} <span className="font-mono text-xs text-[#C79A3B]">{t.reference}</span></div>
              <div className="text-sm text-[#6B7280] truncate">{t.last_from === "team" ? "You: " : ""}{t.last_body}</div>
            </div>
            <div className="text-right shrink-0">
              {t.unread > 0 && <span data-testid={`message-unread-${t.reference}`} className="inline-block bg-[#C79A3B] text-white text-xs font-bold px-2 py-0.5 rounded-full mb-1">{t.unread} new</span>}
              <div className="text-xs text-[#6B7280]">{t.last_at ? new Date(t.last_at).toLocaleDateString("en-GB") : ""}</div>
            </div>
          </button>
        ))}
        {sorted.length === 0 && (
          <div className="border border-dashed border-[#E5E7EB] p-10 text-center text-[#6B7280]">
            <MessageSquare size={24} className="mx-auto mb-3 opacity-50" />
            {q ? "No conversations match your search." : "No client messages yet."}
          </div>
        )}
      </div>
    </div>
  );
}
